<template>
  <main class="training-survey">
    <section class="survey-shell" :class="{ 'survey-shell--intro': isIntroStep }">
      <form @submit.prevent="handlePrimaryAction" class="survey-card">
        <div
          class="header-image"
          :style="{ backgroundImage: `url('https://baessh.com.mx/img/email/encuesta-cap.webp')` }"
        ></div>

        <div class="survey-body">
          <div v-if="showProgress" class="progress-block" aria-live="polite">
            <div class="progress-meta">
              <span>{{ progressLabel }}</span>
              <strong>{{ progressPercent }}%</strong>
            </div>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
          </div>

          <transition name="step-shift" mode="out-in">
            <div v-if="isIntroStep" :key="stepAnimationKey" class="intro-panel">
              <p class="intro-kicker">BAESSH Capacitación</p>
              <h1>Evaluación del curso de capacitación</h1>
              <p class="intro-copy">
                Tu opinión nos ayuda a mejorar la calidad de nuestros cursos,
                materiales y atención durante la capacitación.
              </p>
            </div>

            <div v-else-if="isGeneralStep" :key="stepAnimationKey" class="question-panel">
              <p class="question-counter">Datos generales</p>
              <h2>Cuéntanos sobre ti</h2>
              <div class="general-grid">
                <div class="field-wrap">
                  <label for="empresa">Empresa <span>*</span></label>
                  <input
                    id="empresa"
                    v-model.trim="form.empresa"
                    class="text-input"
                    type="text"
                    maxlength="150"
                    autocomplete="organization"
                    placeholder="Nombre de la empresa"
                  />
                </div>
                <div class="field-wrap">
                  <label for="participante_nombre">Nombre del participante <span>*</span></label>
                  <input
                    id="participante_nombre"
                    v-model.trim="form.participante_nombre"
                    class="text-input"
                    type="text"
                    maxlength="150"
                    autocomplete="name"
                    placeholder="Tu nombre"
                  />
                </div>
                <div class="field-wrap">
                  <label for="participante_puesto">Puesto</label>
                  <input
                    id="participante_puesto"
                    v-model.trim="form.participante_puesto"
                    class="text-input"
                    type="text"
                    maxlength="120"
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <p v-if="showStepError" class="step-error">
                Empresa y nombre son obligatorios.
              </p>
            </div>

            <div v-else-if="isCourseStep" :key="stepAnimationKey" class="question-panel">
              <p class="question-counter">Curso recibido</p>
              <h2>¿Qué curso recibiste?</h2>
              <div class="field-wrap">
                <label class="sr-only" for="curso_id">¿Qué curso recibiste?</label>
                <select
                  id="curso_id"
                  v-model="form.curso_id"
                  class="course-select"
                  :class="{ 'is-invalid': showStepError }"
                  @change="clearStepError"
                >
                  <option value="" disabled>Selecciona el curso recibido</option>
                  <option v-if="isLoadingCourses" value="" disabled>Cargando cursos...</option>
                  <option v-else-if="courses.length === 0" value="" disabled>No hay cursos disponibles</option>
                  <option v-for="course in courses" :key="course.id" :value="course.id">
                    {{ course.nombre }}
                  </option>
                </select>
              </div>
              <p v-if="showStepError" class="step-error">
                Selecciona el curso recibido para continuar.
              </p>
            </div>

            <div v-else :key="stepAnimationKey" class="question-panel">
              <p class="question-counter">Pregunta {{ currentQuestionNumber }} de {{ totalQuestions }}</p>
              <h2>{{ currentQuestion.title }}</h2>

              <div v-if="currentQuestion.type === 'rating'" class="rating-stack">
                <button
                  v-for="option in currentQuestion.options"
                  :key="option.value"
                  type="button"
                  class="answer-button"
                  :class="{
                    selected: form[currentQuestion.key] === option.value,
                    'answer-button--text': !isNumericAnswer(option.value),
                  }"
                  @click="selectAnswer(option.value, $event)"
                >
                  <span class="answer-value">
                    {{ isNumericAnswer(option.value) ? option.value : "N/A" }}
                  </span>
                  <span class="answer-label">{{ option.label }}</span>
                </button>
              </div>

              <div v-else-if="currentQuestion.type === 'choice'" class="choice-stack">
                <button
                  v-for="option in currentQuestion.options"
                  :key="option.value"
                  type="button"
                  class="choice-button"
                  :class="{ selected: form[currentQuestion.key] === option.value }"
                  @click="selectAnswer(option.value, $event)"
                >
                  {{ option.label }}
                </button>
              </div>

              <div v-else class="field-wrap">
                <label class="sr-only" :for="currentQuestion.key">{{ currentQuestion.title }}</label>
                <textarea
                  :id="currentQuestion.key"
                  v-model.trim="form[currentQuestion.key]"
                  class="comment-input"
                  rows="4"
                  maxlength="280"
                  placeholder="Escribe tu respuesta si lo deseas"
                  @input="clearStepError"
                ></textarea>
                <span class="char-count">{{ form[currentQuestion.key].length }}/280</span>
              </div>

              <p v-if="showStepError" class="step-error">
                Selecciona una respuesta para continuar.
              </p>
            </div>
          </transition>

          <div v-if="submitError" class="submit-error" role="alert">
            No pudimos registrar la encuesta. Revisa tu conexión e intenta de nuevo.
          </div>

          <div class="action-row">
            <button
              v-if="showBackButton"
              type="button"
              class="secondary-action"
              :disabled="isSubmitting"
              @click="goBack"
            >
              Atrás
            </button>
            <button
              v-if="showPrimaryButton"
              type="submit"
              class="primary-action"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">Enviando...</span>
              <span v-else>{{ primaryButtonText }}</span>
            </button>
          </div>
        </div>
      </form>
    </section>
  </main>
</template>

<script>
import axiosInstance from "../../services/axiosInstance";

const agreementOptions = [
  { value: 1, label: "Totalmente en desacuerdo" },
  { value: 2, label: "En desacuerdo" },
  { value: 3, label: "Ni de acuerdo ni en desacuerdo" },
  { value: 4, label: "De acuerdo" },
  { value: 5, label: "Totalmente de acuerdo" },
];

export default {
  name: "EncuestaCapacitacion",
  data() {
    return {
      stepIndex: 0,
      cameBack: false,
      isSubmitting: false,
      isLoadingCourses: false,
      showStepError: false,
      submitError: false,
      capacitadorReferencia: "",
      courses: [],
      form: {
        empresa: "",
        participante_nombre: "",
        participante_puesto: "",
        curso_id: "",
        escala_01: null,
        escala_02: null,
        escala_03: null,
        escala_04: null,
        escala_05: null,
        escala_06: null,
        escala_07: null,
        escala_08: null,
        respuesta_01: "",
        respuesta_02: "",
        respuesta_03: "",
        respuesta_04: "",
      },
    };
  },
  computed: {
    ratingQuestions() {
      return [
        {
          key: "escala_01",
          type: "rating",
          title: "¿En general, cómo calificarías el curso?",
          options: [
            { value: 1, label: "Muy deficiente" },
            { value: 2, label: "Deficiente" },
            { value: 3, label: "Regular" },
            { value: 4, label: "Bueno" },
            { value: 5, label: "Excelente" },
          ],
        },
        {
          key: "escala_02",
          type: "rating",
          title: "El contenido del curso fue claro y comprensible",
          options: agreementOptions,
        },
        {
          key: "escala_03",
          type: "rating",
          title: "Las diapositivas y materiales utilizados facilitaron la comprensión del curso",
          options: agreementOptions,
        },
        {
          key: "escala_04",
          type: "rating",
          title: "Los equipos o recursos utilizados durante el curso fueron adecuados",
          options: agreementOptions,
        },
        {
          key: "escala_05",
          type: "rating",
          title: "El capacitador explicó los temas de manera clara",
          options: agreementOptions,
        },
        {
          key: "escala_06",
          type: "rating",
          title: "El capacitador mostró dominio y conocimiento de los temas",
          options: agreementOptions,
        },
        {
          key: "escala_07",
          type: "rating",
          title: "El capacitador mantuvo un trato respetuoso y profesional",
          options: agreementOptions,
        },
        {
          key: "escala_08",
          type: "rating",
          title:
            "Las actividades prácticas, como ejercicios, simulaciones o demostraciones, te ayudaron a comprender y aplicar lo aprendido",
          options: [
            ...agreementOptions,
            {
              value: "no_aplica",
              label: "No se realizaron prácticas, ejercicios, simulaciones o demostraciones",
            },
          ],
        },
      ];
    },
    questions() {
      return [
        ...this.ratingQuestions,
        {
          key: "respuesta_01",
          type: "choice",
          title: "¿Después del curso, te sientes preparado para aplicar lo aprendido?",
          options: [
            { value: "Sí", label: "Sí" },
            { value: "Parcialmente", label: "Parcialmente" },
            { value: "No", label: "No" },
          ],
        },
        {
          key: "respuesta_02",
          type: "choice",
          title: "¿Recomendarías este curso a otras personas?",
          options: [
            { value: "Sí", label: "Sí" },
            { value: "Probablemente", label: "Probablemente" },
            { value: "No", label: "No" },
          ],
        },
        {
          key: "respuesta_03",
          type: "textarea",
          title: "¿Qué podríamos mejorar?",
          required: false,
        },
      ];
    },
    isIntroStep() {
      return this.stepIndex === 0;
    },
    isGeneralStep() {
      return this.stepIndex === 1;
    },
    isCourseStep() {
      return this.stepIndex === 2;
    },
    showProgress() {
      return !this.isIntroStep;
    },
    questionStartStep() {
      return 3;
    },
    totalQuestions() {
      return this.questions.length;
    },
    totalProgressSteps() {
      return 2 + this.totalQuestions;
    },
    currentQuestionIndex() {
      return this.stepIndex - this.questionStartStep;
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null;
    },
    currentQuestionNumber() {
      return this.currentQuestionIndex + 1;
    },
    progressPercent() {
      if (!this.showProgress) return 0;
      return Math.round((this.stepIndex / this.totalProgressSteps) * 100);
    },
    progressLabel() {
      if (this.isGeneralStep) return "Datos generales";
      if (this.isCourseStep) return "Curso recibido";
      return `${this.currentQuestionNumber} de ${this.totalQuestions}`;
    },
    stepAnimationKey() {
      return `survey-step-${this.stepIndex}`;
    },
    showBackButton() {
      return !this.isIntroStep;
    },
    showPrimaryButton() {
      return (
        this.isIntroStep ||
        this.isGeneralStep ||
        this.isCourseStep ||
        this.cameBack ||
        this.currentQuestion?.type === "textarea"
      );
    },
    primaryButtonText() {
      if (this.isIntroStep) return "Comenzar";
      if (this.isGeneralStep || this.isCourseStep || this.cameBack) return "Continuar";
      if (this.currentQuestionNumber === this.totalQuestions) return "Enviar encuesta";
      return "Continuar";
    },
  },
  created() {
    this.capacitadorReferencia =
      this.$route.params.capacitador ||
      this.$route.query.capacitador_id ||
      this.$route.query.capacitador ||
      "";
    this.loadCourses();
  },
  methods: {
    async loadCourses() {
      try {
        this.isLoadingCourses = true;
        const { data } = await axiosInstance.get("/encuesta/capacitacion/cursos");
        if (Array.isArray(data?.cursos) && data.cursos.length > 0) {
          this.courses = data.cursos;
        }
      } catch (error) {
        console.warn("No fue posible cargar cursos desde el catálogo:", error);
      } finally {
        this.isLoadingCourses = false;
      }
    },
    clearStepError() {
      this.showStepError = false;
      this.submitError = false;
    },
    isNumericAnswer(value) {
      return Number.isInteger(Number(value));
    },
    selectAnswer(value, event) {
      if (event?.currentTarget) {
        event.currentTarget.blur();
      }
      this.form[this.currentQuestion.key] = value;
      if (this.currentQuestion.key === "escala_08") {
        this.form.respuesta_04 = value === "no_aplica" ? "No se realizaron prácticas" : "";
      }
      this.clearStepError();

      if (!this.cameBack) {
        window.setTimeout(() => this.advanceFromAnsweredQuestion(), 140);
      }
    },
    advanceFromAnsweredQuestion() {
      if (this.currentQuestionNumber === this.totalQuestions) {
        this.submitSurvey();
        return;
      }
      this.stepIndex += 1;
    },
    handlePrimaryAction() {
      if (this.isIntroStep) {
        this.stepIndex = 1;
        return;
      }

      if (!this.isCurrentStepValid()) {
        this.showStepError = true;
        return;
      }

      if (this.currentQuestionNumber === this.totalQuestions) {
        this.submitSurvey();
        return;
      }

      this.cameBack = false;
      this.stepIndex += 1;
    },
    goBack() {
      if (this.stepIndex > 0) {
        this.stepIndex -= 1;
        this.cameBack = this.stepIndex >= this.questionStartStep;
        this.clearStepError();
      }
    },
    isCurrentStepValid() {
      if (this.isGeneralStep) {
        return Boolean(this.form.empresa && this.form.participante_nombre);
      }
      if (this.isCourseStep) {
        return Boolean(this.form.curso_id);
      }
      if (!this.currentQuestion || this.currentQuestion.required === false) return true;
      const value = this.form[this.currentQuestion.key];
      return value !== null && value !== "";
    },
    async submitSurvey() {
      if (this.isSubmitting) return;

      try {
        this.isSubmitting = true;
        this.submitError = false;

        await axiosInstance.post("/encuesta/capacitacion/enviar", {
          ...this.form,
          capacitador_id: this.capacitadorReferencia || null,
        });

        this.$router.push({ name: "encuestaCapacitacionGracias" });
      } catch (error) {
        this.submitError = true;
        console.error("Error al enviar encuesta de capacitacion:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.training-survey {
  min-height: 100vh;
  background: #f8f9fa;
  color: #172033;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 30px 14px;
}

.survey-shell {
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
}

.progress-block {
  display: grid;
  gap: 7px;
  margin-bottom: 20px;
}

.progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #4f6177;
  font-size: 13px;
}

.progress-track {
  height: 8px;
  border-radius: 999px;
  background: #dce5ee;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #136dec;
  transition: width 0.2s ease;
}

.survey-card {
  background: #ffffff;
  border: 0;
  border-radius: 0.8rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  min-height: calc(100vh - 60px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header-image {
  height: 228px;
  background-size: cover;
  background-position: center;
}

.survey-body {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.intro-panel,
.question-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.intro-panel {
  justify-content: center;
}

.step-shift-enter-active,
.step-shift-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.step-shift-enter {
  opacity: 0;
  transform: translateX(10px);
}

.step-shift-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}

.intro-kicker,
.question-counter {
  color: #136dec;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1,
h2 {
  color: #142033;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.12;
}

h1 {
  font-size: 31px;
  margin-bottom: 14px;
}

h2 {
  font-size: 22px;
  margin-bottom: 18px;
}

.intro-copy {
  color: #526273;
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 16px;
}

.intro-highlights {
  display: grid;
  gap: 9px;
  color: #254260;
  font-size: 14px;
  font-weight: 700;
}

.intro-highlights span {
  display: flex;
  align-items: center;
  gap: 9px;
  background: #f3f7fc;
  border: 1px solid #dce7f4;
  border-radius: 8px;
  padding: 10px 12px;
}

.general-grid,
.field-wrap {
  display: grid;
  gap: 10px;
}

.general-grid {
  gap: 14px;
}

.field-wrap label {
  color: #27384b;
  font-size: 14px;
  font-weight: 800;
}

.field-wrap label span {
  color: #b42318;
}

.course-select,
.comment-input,
.text-input {
  width: 100%;
  border: 1px solid #cfd9e3;
  border-radius: 8px;
  color: #142033;
  background: #ffffff;
  font-size: 16px;
  outline: none;
}

.course-select,
.text-input {
  min-height: 50px;
  padding: 0 14px;
}

.comment-input {
  resize: vertical;
  min-height: 118px;
  padding: 14px;
}

.course-select:focus,
.comment-input:focus,
.text-input:focus {
  border-color: #136dec;
  box-shadow: 0 0 0 3px rgba(19, 109, 236, 0.14);
}

.rating-stack,
.choice-stack {
  display: grid;
  gap: 8px;
}

.answer-button,
.choice-button {
  appearance: none;
  border: 1px solid #d2dce7;
  border-radius: 8px;
  background: #f9fbfd;
  color: #172033;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.answer-button {
  min-height: 50px;
  padding: 8px 12px;
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: center;
  gap: 10px;
  text-align: left;
}

.answer-button--text {
  grid-template-columns: 44px 1fr;
}

.answer-value {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #e7eef8;
  color: #136dec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
}

.answer-button--text .answer-value {
  width: 44px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0;
}

.answer-label {
  font-size: 14px;
  line-height: 1.22;
  overflow-wrap: anywhere;
}

.choice-button {
  min-height: 52px;
  padding: 12px 16px;
  text-align: left;
  font-size: 15px;
  font-weight: 700;
}

.answer-button.selected,
.choice-button.selected {
  border-color: #136dec;
  background: #eef5ff;
  color: #172033;
}

@media (hover: hover) and (pointer: fine) {
  .answer-button:hover,
  .choice-button:hover {
    border-color: #136dec;
    background: #eef5ff;
    color: #172033;
    transform: translateY(-1px);
  }
}

.answer-button.selected .answer-value,
.choice-button.selected {
  background: #136dec;
  color: #ffffff;
}

.step-error,
.submit-error {
  color: #b42318;
  background: #fff1f0;
  border: 1px solid #ffd6d2;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 14px 0 0;
  font-size: 14px;
}

.submit-error {
  margin-top: auto;
}

.char-count {
  color: #6a7888;
  font-size: 12px;
  text-align: right;
}

.action-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  margin-top: 22px;
}

.primary-action,
.secondary-action {
  border-radius: 8px;
  min-height: 50px;
  padding: 0 18px;
  font-weight: 800;
  border: 1px solid transparent;
}

.primary-action {
  background: #136dec;
  border-color: #136dec;
  color: #ffffff;
  grid-column: 2;
}

.secondary-action {
  background: #ffffff;
  color: #30445b;
  border-color: #cfd9e3;
}

.primary-action:disabled,
.secondary-action:disabled {
  opacity: 0.68;
  cursor: not-allowed;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 520px) {
  .training-survey {
    padding: 12px;
  }

  .survey-card {
    min-height: calc(100vh - 24px);
  }

  .header-image {
    height: 148px;
  }

  .survey-body {
    padding: 18px;
  }

  h1 {
    font-size: 27px;
  }

  h2 {
    font-size: 20px;
  }

  .answer-button {
    min-height: 48px;
  }

  .action-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-shift-enter-active,
  .step-shift-leave-active {
    transition: none;
  }

  .step-shift-enter,
  .step-shift-leave-to {
    transform: none;
  }
}
</style>
